import { useRef, useState } from 'react';
import type { ChangeOrder } from '@frontend/AppSettings/services/PipelineProjectService';
import { money, useFinancialVisible } from '@frontend/BidSheets/financialDisplay';
// 2.13's rule: import the stylesheet you render classes from — reachability through the
// pipeline detail's own import is a bundling accident, not a contract.
import '../PipelineProjectDetail.css';

/**
 * The dated Notice of Change a CO's impact lines exist to feed (gap review 2.6; P4b decision #1
 * named this as the lines' purpose, and the demo's ChangeOrderCenter generated exactly this
 * document). Rendered inline on the CO card — the demo's own presentation — and printed via the
 * browser (Print → Save as PDF): the letter-shaped document the xlsx grid precedent does not fit,
 * with no new dependency.
 *
 * Content discipline:
 *  - Only INCLUDED lines appear, and the exclusion of withdrawn lines is SAID, not silent — a
 *    notice that quietly drops evidence reads as a notice that never had it.
 *  - The requested value is the SERVER's sum (co.requested_value), never a client-side re-add.
 *  - The reference is derived from the CO's immutable id, not its list position: a list-index
 *    "NOC-03" renumbers every document when a CO is added. (A human per-project counter is
 *    schema work — deliberately not invented here.)
 *  - Money renders through money(): the card is already SEE_FINANCIALS-served, and the redaction
 *    still applies until capabilities resolve.
 */
interface NoticeOfChangeProps {
  matterid: string;
  co: ChangeOrder;
}

/** Body class scoping the print stylesheet. */
export const NOC_PRINTING_CLASS = 'noc-printing';
/** Marks WHICH notice prints: two CO cards can both hold an open notice, and without a
 * per-instance target both printed superimposed at the page origin (review, proven). */
export const NOC_TARGET_CLASS = 'noc-print-target';

const num = (v: string | null | undefined): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const NoticeOfChange = ({ matterid, co }: NoticeOfChangeProps): JSX.Element => {
  useFinancialVisible();   // the subscription money() mandates — re-render when the gate flips
  const [open, setOpen] = useState(false);
  const docRef = useRef<HTMLDivElement>(null);

  const included = co.lines.filter((l) => l.included);
  const withdrawn = co.lines.length - included.length;
  const reference = `NOC-${matterid}-${co.id.slice(0, 8)}`;

  const print = (): void => {
    const doc = docRef.current;
    if (!doc) return;
    const cleanup = (): void => {
      document.body.classList.remove(NOC_PRINTING_CLASS);
      doc.classList.remove(NOC_TARGET_CLASS);
    };
    document.body.classList.add(NOC_PRINTING_CLASS);
    doc.classList.add(NOC_TARGET_CLASS);
    // Cleanup on afterprint, not synchronously: iOS Safari's print() can RETURN before the
    // snapshot is taken, so a finally-cleanup unscoped the page mid-print (review). Desktop
    // browsers block, firing afterprint before print() returns — same path either way.
    window.addEventListener('afterprint', cleanup, { once: true });
    try {
      window.print();
    } catch {
      window.removeEventListener('afterprint', cleanup);
      cleanup();
    }
  };

  return (
    <div className="pipeline-noc">
      <button
        type="button"
        className="pipeline-secondary-btn"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Hide notice' : 'Notice of Change'}
      </button>
      {open && (
        <div className="pipeline-noc-doc noc-doc" ref={docRef}>
          <header className="pipeline-noc-head">
            <div>
              <h3>Notice of Change</h3>
              <p className="pipeline-noc-ref">{reference}</p>
            </div>
            <div className="pipeline-noc-meta">
              <p>Project: {matterid}</p>
              {/* Labelled GENERATED, because that is what it is — this recomputes per
                  render; the stable identity lives in the reference and created date. */}
              <p>CO opened: {co.created_at?.slice(0, 10) ?? '—'}</p>
              <p>Generated: {new Date().toISOString().slice(0, 10)}</p>
              <p>Status: {co.status}</p>
            </div>
          </header>
          <p className="pipeline-noc-body">
            This notice documents a quantified change to the cost basis of project{' '}
            <strong>{matterid}</strong>: <strong>{co.title}</strong>
            {co.description ? ` — ${co.description}` : ''}. The quantified impact, per the
            attached daily records, is:
          </p>
          <table className="pipeline-table">
            <tbody>
              {included.map((line, i) => (
                <tr key={i}>
                  <td>{line.description}</td>
                  <td className="pipeline-noc-amount">{money(num(line.amount))}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="pipeline-noc-total">
            Requested change-order value: <strong>{money(num(co.requested_value))}</strong>
          </p>
          {withdrawn > 0 && (
            <p className="pipeline-noc-note">
              {withdrawn} withdrawn impact line{withdrawn === 1 ? '' : 's'} excluded from this
              notice.
            </p>
          )}
          <p className="pipeline-noc-note">
            Supporting records: daily field tickets by cost code, production log, weather
            record, force-account register.
          </p>
          <div className="pipeline-form-actions pipeline-noc-print">
            <button type="button" className="pipeline-action-btn" onClick={print}>
              Print / save as PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NoticeOfChange;
